import {Injectable} from "@angular/core";
import {HttpClient, HttpResponse} from "@angular/common/http";
import {
  BehaviorSubject,
  catchError,
  finalize,
  firstValueFrom,
  from,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  tap
} from "rxjs";
import {VideoMeeting, VideoMeetingResponse} from "../models/video-meeting.model";
import {environment} from "src/environments/environment";
import {DataTransformService} from "./data-transform.service";
import {AsthmaTest, AsthmaTestResponse, ChronicDisease, ChronicDiseaseResponse} from "../models/asthma-test.model";
import {map, take} from "rxjs/operators";
import {MoodTest} from "../features/diary/mood-form/mood-form.component";
import {DevicesData} from "../models/devices-data.model";
import {AuditTest} from "../features/diary/audit-form/audit-form.component";
import {FormGroup} from "@angular/forms";
import {ObservationData} from "../models/observation.model";
import {
  FetchClassificatorsService,
  MotivationQuestionnaireTypes,
  MotivationQuestionnaireUnits,
} from "./fetch-classificators.service";
import {QuarterlyFeedback} from "../features/diary/quarterly-feedback/quarterly-feedback.component";
import {HealthPlanActivitiesReport} from "../models/health-plan.model";
import {GeneralFeedback} from "../features/diary/general-feedback/general-feedback.component";
import {MockMeetingsService} from "./mock-meetings.service";
import {ModalService} from "./modal.service";
import {AuthService} from "./auth.service";
import {PatchFormService} from "./patch-form.service";
import {UserInfoService} from "./user-info.service";
import {
  Attachment,
  DeleteActivityReportPayload,
  DeleteDevicePayload,
  DeleteObservations,
  FullPersonWard,
  GeneralRequestResponse,
  MessageToSend,
  Motivation,
  Observation,
  ObservationWithPerson,
  PaymentInfo,
  PersonWard,
  SaveActivityReportPayload,
  SaveDeviceChangePayload,
  SaveMessagePayload,
  SaveMotivationPayload,
  SaveObservationDataPayload,
  SaveObservationPayload,
  SavePersonWardPayload,
} from "../models/payloads/shared/save-request.model";
import {
  CheckMedicationsPayload,
  FetchAddressDetailsPayload,
  FetchAttachmentsPayload,
  FetchContactDetailsPayload,
  FetchDevicesPayload,
  FetchDiseasesPayload,
  FetchHealthPlanPayload,
  FetchInstitutionNamesPayload,
  FetchMeetingsPayload,
  FetchMessagesPayload,
  FetchMessagesPayloadParams,
  FetchMessagesResponse,
  FetchMessageThreadPayload,
  FetchObservationsPayload,
  FetchPayload,
  HealthPlanRelationHelperPayload,
  MedicationPayload,
  MedicationRequestPayload,
  MotivationalQuestionnairePayload,
} from "../models/payloads/shared/fetch-request.model";
import {
  FetchSystemMessagesConfirmationsPayload,
  SaveSystemMessagesAsReadPayload,
} from "../models/payloads/system-messages-confirmation-payloads";
import {FetchSystemMessagesPayload} from "../models/payloads/system-messages-payloads";
import {SystemMessage} from "../models/system-messages/system-message";
import {DeletePersonAuthenticationPayload} from "../models/payloads/person-authentication-payloads";
import {TranslateService} from "@ngx-translate/core";
import {AuthenticateService} from "./authenticate.service";
import {SessionStorageService} from "./session-storage.service";
import {ErrorCodes} from "../shared/constants/error-codes.enum";
import {ResponseCode} from "../models/enum/response-code.model";
import {MsgCode} from "../models/enum/msg-code.model";
import {GlobalErrorAndExceptionHandler} from "../shared/error-handler/global-error-and-exception-handler.service";

@Injectable({
  providedIn: "root",
})
export class GeneralRequestService {
  url: string =
    environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/general-request/";

  backendFitbitCodeUrl: string =
    environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/fitbit-code/";

  backendFitbitUpdateUrl: string =
    environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/update-fitbit-statistics/";

  backendFitbitDeviceCheckUrl: string =
    environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/fitbit-device-check/";

  medicationUpdateUrl: string = environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/medications/";

  serviceStatusUrl: string =
    environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/service-status";

  tokenStatusUrl: string =
    environment.backend.baseUrl +
    environment.backend.contextPath +
    "/rest/token-status";

  videoMeetings = new BehaviorSubject<VideoMeeting[]>(null);
  isLoggingOut: boolean;
  sessionMismatchDetected = false;
  sub: Subscription;
  bloodPressureRequest = new BehaviorSubject<GeneralRequestResponse>(null);
  // show spinner while data fetch is on-going
  loadingStatus = new LoadingSpinnerStatus();
  loadingDataStatus = new BehaviorSubject<LoadingSpinnerStatus>(new LoadingSpinnerStatus());
  motivationQuestionnaireTypes: MotivationQuestionnaireTypes;
  motivationQuestionnaireUnits: MotivationQuestionnaireUnits;
  overallScore: number;
  finishedTest: MoodTest;
  private fallbackModeSubject = new BehaviorSubject<boolean>(false);
  public fallbackMode$ = this.fallbackModeSubject.asObservable();
  private requestResponse = new Subject<GeneralRequestResponse>();
  private asthmaTestList = new BehaviorSubject<AsthmaTest[]>(null);
  private chronicDiseases = new BehaviorSubject<ChronicDisease[]>(null);
  private fitbitDefaultDeviceResponse = new BehaviorSubject<DevicesData>(null);
  private fitbitCodeResponse = new BehaviorSubject<Boolean>(null);

  constructor(
    public http: HttpClient,
    private modalService: ModalService,
    private authService: AuthService,
    private dataTransformService: DataTransformService,
    private patchFormService: PatchFormService,
    private fetchClassificators: FetchClassificatorsService,
    private mockMeetingsService: MockMeetingsService,
    private userInfoService: UserInfoService,
    private translate: TranslateService,
    private authenticateService: AuthenticateService,
    private sessionStorageService: SessionStorageService,
    private globalErrorHandler: GlobalErrorAndExceptionHandler
  ) {

    this.fetchClassificators.hasClassificators.subscribe((data) => {
      if (data == true) {
        this.fetchClassificators.motivationTypes.subscribe((data) => this.motivationQuestionnaireTypes = data);
        this.fetchClassificators.motivationUnits.subscribe((data) => this.motivationQuestionnaireUnits = data);
      }
    });
  }

  saveMessage(saveMsg: MessageToSend) {
    const saveMessageObj: SaveMessagePayload = new SaveMessagePayload([
      saveMsg,
    ]);
    this.postRequest(saveMessageObj);
  }

  async postRequest(obj: any) {

    if (this.sessionMismatchDetected) return;

    try {
      const response = await firstValueFrom(
        this.http.post<GeneralRequestResponse>(this.url, obj, {
          withCredentials: true,
          observe: "response",
          reportProgress: true,
        }),
      );

      const responseCode = response.body?.acknowledgment?.responseCode;
      if (response.status === 200 && responseCode === "ACC") {
        this.modalService.requestResponseSuccess.next(true);
        this.requestResponse.next(response.body);
      } else {
        this.modalService.requestResponseSuccess.next(false);
        this.globalErrorHandler.handleBusinessResponse(response, ErrorCodes.ERR001)
      }
    } catch (err) {
        this.modalService.requestResponseSuccess.next(false);
        this.globalErrorHandler.handleAndDisplayError(err, ErrorCodes.ERR002)
    }
  }

  private async fetchMessage(payload: FetchPayload) {
    this.sub = this.authService
      .getIsLoggingOut()
      .pipe(take(1))
      .subscribe((isloggingOut) => {
        // console.log("isLoggingOut value in sub in fetchMessage", isloggingOut);
        this.isLoggingOut = isloggingOut;
      });

    const response = await this.http.post<FetchMessagesResponse>(this.url, payload, {
      withCredentials: true,
      observe: "response"
    }).toPromise();

    const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          return response.body.items;
        } else if (
          response.status === 200 && (responseCode === "ERR" || responseCode === "REJ") && !this.isLoggingOut
        ) {
          const messageCode = response.body.acknowledgment.acknowledgmentDetailList ?
            response.body.acknowledgment.acknowledgmentDetailList[0].messageCode : this.translate.instant("errorBackup");
          this.modalService.closeActiveDialogRef();
          this.modalService.showErrorModal(messageCode, ErrorCodes.ERR005, true);
        }
        return null;
  }

  async fetchMessageThread(messageId: number) {
    try {
      return await this.fetchMessage(new FetchMessageThreadPayload(messageId.toString()));
    } catch (err) {
      this.globalErrorHandler.handleAndDisplayError(err, ErrorCodes.ERR007);
      return null;
    }
  }

  async fetchAllLatestMessages(limit: number, isAutoFetch : boolean = false) {
    const payloadParams: FetchMessagesPayloadParams = {
      paginationLimit: limit,
      directionCodeId: [
        JSON.parse(sessionStorage.getItem("MESSAGE_DIRECTION_HCP_PATIENT")),
        JSON.parse(sessionStorage.getItem("MESSAGE_DIRECTION_PATIENT_HCP")),
      ],
      offset: 0,
    };

    try {
      const [sentToMeMessages, sentByMeMessages] = await Promise.all([
        this.fetchMessage(new FetchMessagesPayload(payloadParams, 0, "Message.sentToPersonId")),
        this.fetchMessage(new FetchMessagesPayload(payloadParams, 1, "Message.sentByPersonId")),
      ]);

      return [...(sentToMeMessages || []), ...(sentByMeMessages || [])];
    } catch (error) {
      if (isAutoFetch) {
        console.error("[Auto-Fetch]", error);
      }
      return null;
    }
  }

  getRequestResponse(): Observable<GeneralRequestResponse> {
    return this.requestResponse.asObservable();
  }

  getVideoMeetings(): Observable<VideoMeeting[]> {
    return this.videoMeetings.asObservable();
  }

  getAsthmaTests(): Observable<AsthmaTest[]> {
    if (!this.asthmaTestList.value) {
      this.fetchAsthmaTests();
    }
    return this.asthmaTestList.asObservable();
  }

  getChronicDiseases(): Observable<ChronicDisease[]> {
    return this.chronicDiseases.asObservable();
  }

  fetchAddresses(institutionId: number) {
    this.postRequest(new FetchAddressDetailsPayload(institutionId));
  }

  fetchContacts(institutionId: number) {
    this.postRequest(new FetchContactDetailsPayload(institutionId));
  }

  fetchInstitutionNamesBatch(registerIds: number[]) {
    const payload = new FetchInstitutionNamesPayload(registerIds);
    this.postRequest(payload);
    return this.getRequestResponse();
  }

  fetchMeetings(isAutoFetch : boolean = false) {
    const fetchMeetingsPayload = new FetchMeetingsPayload();
    return this.http
      .post<VideoMeetingResponse>(this.url, fetchMeetingsPayload, {
        withCredentials: true,
        observe: "response"
      })
      .toPromise()
      .then((response: any) => {
        // console.log("fetchMeetings response: ", response);
        const responseCode = response.body.acknowledgment.responseCode;

        if (response.status === 200 && responseCode === "ACC") {
          this.videoMeetings.next(response.body.items);
        } else if (
          response.status === 200 &&
          (responseCode === "ERR" || responseCode === "REJ")
        ) {
          // this.modalService.showErrorModal(
          //  response.body.acknowledgment.acknowledgmentDetailList[0].messageCode
          //  );
        }
      })
      .catch((err) => {
        if (isAutoFetch) {
          console.error("[Auto-Fetch]", err);
        }
      });
  }

  fetchAttachments(id: string) {
    const fetchAttachmentsPayload = new FetchAttachmentsPayload(id);
    return this.http
      .post<GeneralRequestResponse>(this.url, fetchAttachmentsPayload, {
        observe: "response",
        withCredentials: true,
      })
      .toPromise()
      .then((response) => {
        // console.log("fetchAttachments response: ", response);
        const attachments: Attachment[] = response.body.items;
        return attachments;
      })
      .catch((err) => {
        // console.log('Error: ', err);
      });
  }

  generateMockMeetings() {
    return this.mockMeetingsService.getMockMeetings();
  }

  fetchAsthmaTests() {
    const asthmaTestClassificator = JSON.parse(
      sessionStorage.getItem("AKT_AKT"),
    );

    const fetchAsthmaTestsPayload = new FetchDiseasesPayload(
      [asthmaTestClassificator],
      50,
      ["observationList"],
    );
    return this.http
      .post<GeneralRequestResponse>(this.url, fetchAsthmaTestsPayload, {
        withCredentials: true,
        observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log("fetchAsthmaTests response: ", response);
        const responseCode = response.body.acknowledgment.responseCode;

        if (response.status === 200 && responseCode === "ACC") {
          const asthmaTests = this.dataTransformService.responseToAsthmaTestArray(
            response.body.items,
          );
          this.asthmaTestList.next(asthmaTests);
        } else if (
          response.status === 200 &&
          (responseCode === "ERR" || responseCode === "REJ")
        ) {
          // console.log('asthma test fetch error');
        }
      });
  }

  saveAsthmaTest(newTest: AsthmaTest) {
    const asthmaTestId: number = JSON.parse(sessionStorage.getItem("AKT_AKT"));
    const asthmaObs: Observation = new Observation(
      asthmaTestId,
      [this.dataTransformService.createAsthmaTestObservation(newTest)],
      null,
    );
    const saveTestObj: SaveObservationPayload = new SaveObservationPayload([
      asthmaObs,
    ]);
    this.postRequest(saveTestObj);
    this.getRequestResponse()
      .pipe(take(1))
      .subscribe((res: AsthmaTestResponse) => {
        if (res.items[0].classificatorCodeId === asthmaTestId) {
          newTest.originalObservationId = res.items[0].id;
          this.getAsthmaTests()
            .pipe(take(1))
            .subscribe((tests: AsthmaTest[]) => {
              if (tests && tests.length > 0) {
                tests.unshift(newTest);
              } else {
                tests = [newTest];
              }
              this.asthmaTestList.next(tests);
            });
        }
      });
  }

  saveMoodTest(newTest: MoodTest) {
    const moodTestId = JSON.parse(sessionStorage.getItem("EEK2_EEK2"));
    const moodObs: Observation = new Observation(
      moodTestId,
      this.dataTransformService.createMoodTestObservationList(newTest),
      null,
    );
    const saveTestObj: SaveObservationPayload = new SaveObservationPayload([
      moodObs,
    ]);
    this.postRequest(saveTestObj);
    this.getRequestResponse()
      .pipe(take(1))
      .subscribe((res) => {
        if (res.items[0].classificatorCodeId === moodTestId) {
          newTest.originalObservationId = res.items[0].id;
          const testFromForm: MoodTest = this.patchFormService.getMoodTest();
          testFromForm.originalObservationId = res.items[0].id;
          this.patchFormService.sendMoodTestToReview(testFromForm);
        }
      });
  }

  saveAuditTest(auditTest: AuditTest[], overallScore) {
    const auditTestId = JSON.parse(sessionStorage.getItem("AUDIT_AUDIT"));
    const auditObs: Observation = new Observation(
      auditTestId,
      [this.dataTransformService.createAuditTestObservationList(auditTest, overallScore)],
      null,
    );
    const saveAuditObj: SaveObservationPayload = new SaveObservationPayload([
      auditObs,
    ]);
    this.postRequest(saveAuditObj);
  }

  saveQtlyFeedback(quarterlyFeedback: QuarterlyFeedback[], overallScore) {
    const quarterlyFeedbackId = JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_FEEDBACK"));

    const quarterlyFeedbackObs: Motivation = new Motivation(
      quarterlyFeedbackId,
      [
        new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_FEEDBACK_GPR")), [
          new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_2")), null, quarterlyFeedback[1].answer, null, null),
          new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_3")), null, quarterlyFeedback[2].answer, null, null),
          new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_4")), null, quarterlyFeedback[3].answer, null, null),
          new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_5")), null, quarterlyFeedback[4].answer, null, null),
          new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_6")), null, quarterlyFeedback[5].answer, null, null),
        ], overallScore, null, null),
        new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_1")), null, quarterlyFeedback[0].answer, null, null),
        new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_7")), null, quarterlyFeedback[6].answer, null, null),
        new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_8")), null, quarterlyFeedback[7].answer, null, null),
        new Motivation(JSON.parse(sessionStorage.getItem("QTLY_FEEDBACK_QTLY_9")), null, quarterlyFeedback[8].suggestion, null, null),
      ],
      null, null, null,
    );
    const savequarterlyFeedbackObj: SaveObservationPayload = new SaveObservationPayload([
      quarterlyFeedbackObs,
    ]);
    this.postRequest(savequarterlyFeedbackObj);
  }

  saveGeneralFeedback(generalFeedback: GeneralFeedback[]) {
    const generalFeedbackId = JSON.parse(sessionStorage.getItem("GNRL_FEEDBACK"));
    const generalFeedbackObs: Motivation = new Motivation(
      generalFeedbackId,
      [
        new Motivation(JSON.parse(sessionStorage.getItem("GNRL_FEEDBACK_FEED_1")), null, generalFeedback[0].rate, null, null),
        new Motivation(JSON.parse(sessionStorage.getItem("GNRL_FEEDBACK_FEED_2")), null, generalFeedback[1].proposal, null, null),
        new Motivation(JSON.parse(sessionStorage.getItem("GNRL_FEEDBACK_FEED_3")), null, generalFeedback[2].answer, null, null),
        new Motivation(JSON.parse(sessionStorage.getItem("GNRL_FEEDBACK_FEED_4")), null, generalFeedback[3].suggestion, null, null),
      ], null, null, null,
    );
    const saveGeneralFeedbackObj: SaveObservationPayload = new SaveObservationPayload([
      generalFeedbackObs,
    ]);
    this.postRequest(saveGeneralFeedbackObj);
  }

  saveMotivationQuestionnaireToDB(motForm: FormGroup) {
    const motivationId = JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_MOTIVATION_TEST"));
    const motivationObs: Motivation = new Motivation(
      motivationId,
      [
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_1")),
          [],
          motForm.get("heightWeight.height").value,
          this.motivationQuestionnaireTypes.number,
          this.motivationQuestionnaireUnits.cm,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_2")),
          [],
          motForm.get("heightWeight.weight").value,
          this.motivationQuestionnaireTypes.number,
          this.motivationQuestionnaireUnits.kg,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_3")),
          [],
          motForm.get("weightSatisfaction.inIdealWeight").value,
          this.motivationQuestionnaireTypes.yesNoAnswer,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_4")),
          [],
          motForm.get("weightSatisfaction.desiredWeight").value,
          this.motivationQuestionnaireTypes.number,
          this.motivationQuestionnaireUnits.kg,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_5")),
          [],
          motForm.get("habits.averageSleep.aggregateMinutes").value,
          this.motivationQuestionnaireTypes.number,
          null,
          // MOTIVATION_TEST_6 IS EMPTY
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_7")),
          [],
          motForm.get("habits.nicotine.cigarettes").value,
          this.motivationQuestionnaireTypes.number,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_21")),
          [],
          motForm.get("habits.nicotine.yearsSmoking").value,
          this.motivationQuestionnaireTypes.number,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_22")),
          [],
          motForm.get("habits.nicotine.otherNicotineProducts").value ? "yes" : "no",
          this.motivationQuestionnaireTypes.yesNoAnswer,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_8")),
          [],
          motForm.get("habits.consumeAlcoholDaily").value,
          this.motivationQuestionnaireTypes.yesNoAnswer,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_9")),
          [],
          motForm.get("habits.stress").value,
          this.motivationQuestionnaireTypes.yesNoAnswer,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_10")),
          [],
          motForm.get("physicalActivity.exercisingRegularly").value,
          this.motivationQuestionnaireTypes.yesNoAnswer,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_11")),
          [],
          JSON.stringify(motForm.get("physicalActivitiesArray").value),
          this.motivationQuestionnaireTypes.text,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_12")),
          [],
          motForm.get("physicalActivity.activitiesPerWeek").value,
          this.motivationQuestionnaireTypes.number,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_13")),
          [],
          (motForm.get("physicalActivity.activityDuration.hour").value *
            60 +
            motForm.get("physicalActivity.activityDuration.minute")
              .value),
          this.motivationQuestionnaireTypes.number,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_14")),
          [],
          JSON.stringify(motForm.get("exerciseActivitiesArray").value),
          this.motivationQuestionnaireTypes.text,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_15")),
          [],
          motForm.get("exerciseActivities.activitiesPerWeek").value,
          this.motivationQuestionnaireTypes.number,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_16")),
          [],
          (motForm.get("exerciseActivities.exerciseDuration.hour")
              .value *
            60 +
            motForm.get("exerciseActivities.exerciseDuration.minute")
              .value),
          this.motivationQuestionnaireTypes.number,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_17")),
          [],
          JSON.stringify(motForm.get("obstaclesArray").value),
          this.motivationQuestionnaireTypes.text,
          null,
        ),
        new Motivation(
          JSON.parse(sessionStorage.getItem("MOTIVATION_TEST_18")),
          [],
          JSON.stringify(motForm.get("motivationArray").value),
          this.motivationQuestionnaireTypes.text,
          null,
        ),
        // new Motivation(motKey19,motForm.get("heightWeight.ab0").value, null, 88, 93),
        // new Motivation(motKey20,motForm.get("heightWeight.ab1").value, null, 88, 93),
      ],
      null,
      null,
      null,
    );
    const saveMotivationObj: SaveMotivationPayload = new SaveMotivationPayload([
      motivationObs,
    ]);
    this.postRequest(saveMotivationObj);
  }

  async saveHeightWeight(height: number, weight: number) {
    const personId = this.userInfoService.getSessionContext().principal.id;
    const weightClassificator = JSON.parse(sessionStorage.getItem("MEASUREMENT_TYPE_1"));
    const heightClassificator = JSON.parse(sessionStorage.getItem("MEASUREMENT_TYPE_2"));
    const textTypeId = JSON.parse(sessionStorage.getItem("VALUE_TYPE_4"));
    const unitCm = JSON.parse(sessionStorage.getItem("UNIT_OF_MEASURE_2"));
    const unitKg = JSON.parse(sessionStorage.getItem("UNIT_OF_MEASURE_1"));
    const childObjWeight = new ObservationWithPerson(weightClassificator, [], weight, personId);
    const childObjHeight = new ObservationWithPerson(heightClassificator, [], height, personId);
    let childObservations: Observation[] = [];
    childObservations.push(childObjWeight);
    childObservations.push(childObjHeight);
    const masterObj = new ObservationWithPerson(weightClassificator, childObservations, null, personId);
    childObjWeight["valueTypeId"] = childObjHeight["valueTypeId"] = textTypeId;
    childObjWeight["effectiveTime"] = childObjHeight["effectiveTime"] = masterObj["effectiveTime"] = new Date().toISOString();
    childObjHeight["valueUnitId"] = unitCm;
    childObjWeight["valueUnitId"] = unitKg;

    const payload = new SaveObservationPayload([masterObj]);

    this.postRequest(payload).then(() => {
      // console.log("SaveHeightWeight response: {}", response);
    });
  }

  fetchDiseases() {
    const diseaseIds = Object.keys(sessionStorage)
      .filter(key => key.startsWith("DISEASE_"))
      .map(key => sessionStorage[key]);

    const fetchDiseasePayload = new FetchDiseasesPayload(
      diseaseIds, 50, null,
    );
    return this.http.post<ChronicDiseaseResponse>(this.url, fetchDiseasePayload, {
      withCredentials: true,
      observe: "response",
    })
      .toPromise()
      .then((response) => {
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          const chronicDiseases = this.dataTransformService.responseToDiseaseArray(response.body.items);
          sessionStorage.setItem("diseases", JSON.stringify(chronicDiseases));
          this.chronicDiseases.next(chronicDiseases);
        } else if (
          response.status === 200 &&
          (responseCode === "ERR" || responseCode === "REJ")
        ) {
          // console.log('disease fetch error');
        }
      });
  }

  async checkFitbitDefaultDevice() {
    return await this.http
      .get<DevicesData>(this.backendFitbitDeviceCheckUrl, {
        withCredentials: true,
        observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log("NB! General-Request checkFitbitDefaultDevice response.body => " + JSON.stringify(response.body));
        if (response.body != null) {
          sessionStorage.setItem("fitbitConnected", "true");
        } else {
          sessionStorage.setItem("fitbitConnected", "false");
        }
        this.fitbitDefaultDeviceResponse.next(response.body);
      })
      .catch((err) => {
        // console.log('NB! checkFitbitDefaultDevice error: ', err);
      });
  }

  getFitbitDefaultDeviceResponse(): Observable<DevicesData> {
    return this.fitbitDefaultDeviceResponse.asObservable();
  }

  async sendFitbitCode(obj: any) {
    return await this.http
      .post<any>(this.backendFitbitCodeUrl, obj, {
        withCredentials: true,
        observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log("NB! GeneralRequest sendFitbitCode response => " + JSON.stringify(response.body));
        this.fitbitCodeResponse.next(response.body);
        return response.body;
      })
      .catch((err) => {
        console.error("GeneralRequest sendFitbitCode error: ", err);
      });
  }

  getFitbitCodeResponse(): Observable<Boolean> {
    return this.fitbitCodeResponse.asObservable();
  }

  async startUpdate() {
    return await this.http
      .get<any>(this.backendFitbitUpdateUrl, {
        withCredentials: true,
        observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log("NB! GeneralRequest startUpdate response => {}", JSON.stringify(response));
      })
      .catch((err) => {
        // console.log('UI startUpdate error: {}', err.toString());
      });
  }

  /**
   * @param offset
   * @param personId
   * @param classificators
   */
  fetchObservationData(offset: number, personId, classificators: number[]) {
    this.loadingStatus.isObservationsDataLoading = true;
    this.loadingDataStatus.next(this.loadingStatus);
    const fetchObservationsPayloadData = new FetchObservationsPayload(offset, [""], personId, classificators);
    return this.http
      .post<GeneralRequestResponse>(this.url, fetchObservationsPayloadData, {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log('fetching observations from... ' + response.body.pagination.offset + ' to ' + (response.body.pagination.limit + response.body.pagination.offset) + ' totalCount: ' + response.body.pagination.count);
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          this.loadingStatus.isObservationsDataLoading = false;
          this.loadingDataStatus.next(this.loadingStatus);
          // console.log('observations fetch error');
          return response.body;
        }
      });
  }

  // Fetch all devices used by user
  fetchDevices(personId: number) {
    this.loadingStatus.isDevicesDataLoading = true;
    this.loadingDataStatus.next(this.loadingStatus);
    const fetchDevicesPayloadData = new FetchDevicesPayload(100, [""], personId);
    return this.http
      .post<GeneralRequestResponse>(this.url, fetchDevicesPayloadData, {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log('fetch devices response: ', response);
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          this.loadingStatus.isDevicesDataLoading = false;
          this.loadingDataStatus.next(this.loadingStatus);
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          return response.body;
        }
      });
  }

  postNewObservation(newObs: ObservationData) {
    this.loadingStatus.isObservationsDataLoading = true;
    this.loadingDataStatus.next(this.loadingStatus);
    return this.http
      .post<GeneralRequestResponse>(this.url, new SaveObservationDataPayload([newObs]), {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log('posting new observation... ', response);
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('posting new observation error');
          return response.body;
        }
      });
  }

  deleteObservation(oldObs: ObservationData) {
    const pressureObs: DeleteObservations = new DeleteObservations([this.dataTransformService.deleteObservation(oldObs)]);
    return this.http
      .post<GeneralRequestResponse>(this.url, pressureObs, {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          // console.log('removing observation... ' + oldObs.id);
          response.body.delete = true;
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('removing error');
          return response.body;
        }
      });
  }

  // Creates new device or updates existing one
  updateDevices(devices: DevicesData[]) {
    const postDevice = new SaveDeviceChangePayload(devices);
    return this.http
      .post<GeneralRequestResponse>(this.url, postDevice, {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          // console.log('updating device... ');
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('updating device error');
          return response.body;
        }
      });
  }

  deleteDevice(device: DevicesData) {
    const deleteDevice = new DeleteDevicePayload([device]);
    return this.http
      .post<GeneralRequestResponse>(this.url, deleteDevice, {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          // console.log('removing device... ' + device.id);
          response.body.delete = true;
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('removing error');
          return response.body;
        }
      });
  }

  fetchHealthPlanData(personId: number) {
    this.loadingStatus.isHealthPlanDataLoading = true;
    this.loadingDataStatus.next(this.loadingStatus);
    const healthPlan = new FetchHealthPlanPayload(100, [""], personId);
    return this.http
      .post<GeneralRequestResponse>(this.url, healthPlan, {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          // console.log('fetching health plan for personId... ' + personId);
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('fetching health plan error');
          return response.body;
        }
      });
  }

  postActivityReport(report: HealthPlanActivitiesReport) {
    return this.http
      .post<GeneralRequestResponse>(this.url, new SaveActivityReportPayload([report]), {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log('posting activity report... ', response);
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('posting activity report error');
          return response.body;
        }
      });
  }

  deleteActivityReport(report: HealthPlanActivitiesReport) {
    return this.http
      .post<GeneralRequestResponse>(this.url, new DeleteActivityReportPayload([report]), {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log('deleting activity report... ', response);
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('deleting activity report error');
          return response.body;
        }
      });
  }

  async fetchMedicationRequestData(prescribedMedications: number[]) {
    this.loadingStatus.isMedicationDataLoading = true;
    this.loadingDataStatus.next(this.loadingStatus);
    const response = await this.http
      .post<GeneralRequestResponse>(this.url, new MedicationRequestPayload(0, [""], prescribedMedications), {
        withCredentials: true, observe: "response",
      })
      .toPromise();
    // console.log('fetching prescribed medications... ' + prescribedMedications, response.body);
    const responseCode = response.body.acknowledgment.responseCode;
    if (response.status === 200 && responseCode === "ACC") {
      return response.body;
    } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
      // console.log('fetching prescribed medications error... ' + prescribedMedications);
      return response.body;
    }
  }

  async fetchRegularMedicationData(regularMedications: number[]) {
    this.loadingStatus.isMedicationDataLoading = true;
    this.loadingDataStatus.next(this.loadingStatus);
    const response = await this.http
      .post<GeneralRequestResponse>(this.url, new MedicationPayload(0, [""], regularMedications), {
        withCredentials: true, observe: "response",
      })
      .toPromise();
    // console.log('fetching regular medications... ' + regularMedications, response);
    const responseCode = response.body.acknowledgment.responseCode;
    if (response.status === 200 && responseCode === "ACC") {
      return response.body;
    } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
      // console.log('fetching regular medications error... ' + regularMedications);
      return response.body;
    }
  }

  fetchMotivationalQuestionnaire(personId: number, questionnaireClassificatorCode: number) {
    return this.http
      .post<GeneralRequestResponse>(this.url, new MotivationalQuestionnairePayload(0, [""], personId, questionnaireClassificatorCode), {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log('fetching motivational questionnaire for person... ' + personId + ' with code: ' + questionnaireClassificatorCode);
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('fetching motivational questionnaire error for person... ' + personId + response);
          return response.body;
        }
      });
  }

  fetchPeriodicQuestionnaire(personId: number, questionnaireClassificatorCode: number) {
    return this.http
      .post<GeneralRequestResponse>(this.url, new MotivationalQuestionnairePayload(0, [""], personId, questionnaireClassificatorCode), {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log('fetching periodic questionnaire for person... ' + personId + ' with code: ' + questionnaireClassificatorCode);
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('fetching periodic questionnaire error for person... ' + personId + response);
          return response.body;
        }
      });
  }

  /**
   * Helper fetch method to deal with health plan versioning relations
   */
  async fetchHealthPlanRelation(offset: number, includeRelation: string[], id: number[], personId: number, tableName: string) {
    return this.http
      .post<GeneralRequestResponse>(this.url, new HealthPlanRelationHelperPayload(offset, includeRelation, id, personId, tableName), {
        withCredentials: true, observe: "response",
      })
      .toPromise()
      .then((response) => {
        // console.log('fetching relation to healthPlan versioning data for personId... ' + personId + ' from ' + tableName);
        const responseCode = response.body.acknowledgment.responseCode;
        if (response.status === 200 && responseCode === "ACC") {
          return response.body;
        } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
          // console.log('fetching relation to healthPlan versioning data for... ' + personId);
          return response.body;
        }
      });
  }

  updateMedicationDataFromMisp(personalCode: string, from?: Date, until?: Date): Observable<any> {
    this.setLoadingStatus(true);
    const payload = new CheckMedicationsPayload(personalCode, from, until);

    return this.http.post<GeneralRequestResponse>(this.medicationUpdateUrl, payload, {
      withCredentials: true,
      observe: "response"
    }).pipe(
      switchMap((response) => {
        if (!response || response.status !== 200) {
          return of(response?.body || null);
        }

        const responseCode = response.body.acknowledgment.responseCode;

        if (responseCode === ResponseCode.SUCCESS) {
          return of(this.handleSuccessResponse(response.body));
        }

        if ([ResponseCode.ERROR, ResponseCode.REJECTED, ResponseCode.GENERAL_ERROR].includes(responseCode as ResponseCode)) {
          return this.handleErrorResponse(response.body.acknowledgment.acknowledgmentDetailList[0]);
        }

        return of(response.body);
      }),
      catchError((error) => {
        console.error('GeneralRequestService error:', error);
        return of(null);
      }),
      finalize(() => {
        this.setLoadingStatus(false);
      })
    );
  }

  private setLoadingStatus(isLoading: boolean): void {
    this.loadingStatus.isMedicationDataLoading = isLoading;
    this.loadingDataStatus.next(this.loadingStatus);
  }

  private handleSuccessResponse(responseBody: GeneralRequestResponse): GeneralRequestResponse {
    this.setFallbackMode(false);
    return responseBody;
  }

  private handleErrorResponse(acknowledgmentDetail: any): Observable<null> {
    const { messageCode, message } = acknowledgmentDetail;

    const shouldClearPrescriptions =
      messageCode === MsgCode.INVALID_NT_REFERENCE ||
      messageCode === MsgCode.FALLBACK_TO_PRESCRIPTION ||
      messageCode === MsgCode.TECHNICAL_ERROR;

    if (shouldClearPrescriptions) {
      this.sessionStorageService.removeStoredPrescriptionsFromSession();

      if (messageCode === MsgCode.INVALID_NT_REFERENCE) {
        return from (this.authenticateService.getSessionContext(false)).pipe(
          tap(() => {
            this.setFallbackMode(false);
          }),
          map(() =>null)
        );
      }
      if (messageCode === MsgCode.FALLBACK_TO_PRESCRIPTION ||
        messageCode === MsgCode.TECHNICAL_ERROR) {
        this.setFallbackMode(true);
        return of(null);
      }
    }
    this.modalService.showErrorModal("errorBackup", ErrorCodes.ERR004, true);
    return of(null);
  }

  setFallbackMode(enabled: boolean) {
    this.fallbackModeSubject.next(enabled);
    this.sessionStorageService.setFallbackToPrescriptions(enabled);
  }


  fetchWardData(personalCode: string): Observable<FullPersonWard[]> {
    // console.log("NB! starting checkPersonalCode for " + personalCode);
    const obj = {
      mode: "FETCH",
      filter: {
        type: "filter",
        element: [
          {
            type: "field",
            name: "PersonWard.wardPersonalCode",
            value: [personalCode],
          },
          {
            type: "field",
            name: "PersonWard.personId",
            value: [this.userInfoService.getSessionContext().principal.id],
          },
        ],
      },
    };
    return this.http
      .post<GeneralRequestResponse>(this.url, obj, {
        observe: "response",
        withCredentials: true,
      })
      .pipe(map(
        response => {
          const responseCode = response.body.acknowledgment.responseCode;
          if (response.status === 200 && responseCode === "ACC") {
            return response.body.items;
          } else {
            return null;
          }
        }
      ));
  }

  async saveWardData(firstName: string, lastName: string, wardPersonalCode: string) {
    const wardInfo: PersonWard = new PersonWard(
      this.userInfoService.getSessionContext().principal.id,
      firstName,
      lastName,
      wardPersonalCode,
    );
    const saveWardInfo: SavePersonWardPayload = new SavePersonWardPayload([wardInfo]);

    try {
      return await this.postRequest(saveWardInfo);
    } catch (err) {
      console.error("An error occurred:", err);
    }
  }

  async updateWardData(wardId: string, firstName: string, lastName: string) {
    const obj = {
      mode: "SAVE_PARTIAL",
      items: [
        {
          id: wardId,
          type: "personWard",
          wardFirstName: firstName,
          wardLastName: lastName,
        },
      ],
    };
    await this.postRequest(obj);
    const sub = this.getRequestResponse()
      .subscribe((body) => {
        if (body.mode === "OK") {
          // console.log("updateWarData successful!");
        }
        sub.unsubscribe();
      });
  }

  getJwtForMeeting(roomCode: string): Observable<string> {
    return this.http.get(`${environment.backend.baseUrl}${environment.backend.contextPath}/rest/get-meeting-token/${roomCode}`, {
      withCredentials: true,
      observe: "response",
      responseType: "text",
    }).pipe(
      map((response: HttpResponse<string>) => response.body),
    );
  }

  async markNotificationAsRead(message: SystemMessage, personId: number) {
    const saveSystemMessagesAsReadPayloadData = new SaveSystemMessagesAsReadPayload(message.id, personId);

    try {
      const response = await firstValueFrom(this.http.post<GeneralRequestResponse>(this.url, saveSystemMessagesAsReadPayloadData, {
        withCredentials: true,
        observe: "response",
      }));
      const responseCode = response.body.acknowledgment.responseCode;
      if (response.status === 200) {
        if (responseCode === "ACC") {
          return response.body;
        } else if (responseCode === "ERR" || responseCode === "REJ") {
          console.log("post system message as read error");
          return response.body;
        }
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  }

  async fetchSystemMessages(isLoggedInFilter: boolean = true) {
    const fetchSystemMessagesPayloadData = new FetchSystemMessagesPayload(100, [""], isLoggedInFilter);
    try {
      const response = await firstValueFrom(this.http.post<GeneralRequestResponse>(this.url, fetchSystemMessagesPayloadData, {
        withCredentials: true,
        observe: "response",
      }));
      const responseCode = response.body.acknowledgment.responseCode;
      if (response.status === 200) {
        if (responseCode === "ERR" || responseCode === "REJ") {
          console.log("fetch system messages error");
        }
        return response.body;
      }
    } catch (error) {
      console.error("Error fetching system messages:", error);
    }
  }

  async fetchSystemMessageConfirmations(personId: number) {
    const FetchSystemMessagesConfirmationsPayloadData = new FetchSystemMessagesConfirmationsPayload(100, [""], personId);
    const response = await firstValueFrom(this.http.post<GeneralRequestResponse>(this.url,
      FetchSystemMessagesConfirmationsPayloadData, {
        withCredentials: true,
        observe: "response",
      }));
    const responseCode = response.body.acknowledgment.responseCode;
    if (response.status === 200) {
      if (responseCode === "ERR" || responseCode === "REJ") {
        console.log("fetch system message confirmations error");
      }
      return response.body;
    }
  }

  deletePersonAuthentication(id: number) {
    const payload = new DeletePersonAuthenticationPayload(id);
    return firstValueFrom(this.http
      .post<GeneralRequestResponse>(this.url, payload, {
        withCredentials: true,
        observe: "response",
      }));
  }

  // check for premium services
  async fetchPremiumServices() {
    if (!this.userInfoService.getSessionContext().register?.id) return;

    const payload = {
      mode: "FETCH",
      filter: {
        type: "filter",
        element: [
          {
            type: "field",
            name: "InstitutionSettings.institution.registerInstitutionList.registerId",
            value: [this.userInfoService.getSessionContext().register.id],
          },
          {
            type: "field",
            name: "InstitutionSettings.institution.registerInstitutionList.valid",
            value: ["true"],
          },
        ],
      },
    };

    try {
      const response = await firstValueFrom(
        this.http.post<GeneralRequestResponse>(this.url, payload, {
          withCredentials: true,
          observe: "response",
        }),
      );

      const responseCode = response.body.acknowledgment.responseCode;
      if (response.status === 200 && responseCode === "ACC") {
        return response.body;
      } else if (response.status === 200 && (responseCode === "ERR" || responseCode === "REJ")) {
        return response.body;
      }
    } catch (error) {
      console.error("Error fetching premium services:", error);
      throw error;
    }
  }

// send service type and receive payment status
  async fetchOrderResponse(serviceTypeId: number) {
    const lang = sessionStorage.getItem("lang");
    const payload = { serviceTypeId, lang };
    try {
      const response = await firstValueFrom(
        this.http.post<any>(this.serviceStatusUrl, payload, {
          withCredentials: true,
          observe: "response",
        }),
      );

      if (response.status === 200 && response.statusText === "OK") {
        return response.body;
      } else if (response.status === 200 && response.statusText !== "OK") {
        return response.body;
      }
    } catch (error) {
      console.error("Error fetching order response:", error);
      throw error;
    }
  }

  // send order token and receive payment status
  async fetchPaymentResponse(saveMessageObj: SaveMessagePayload, orderToken: string): Promise<PaymentInfo> {
    const payload = {
      request: saveMessageObj,
      orderToken: orderToken
    };

    try {
      const response = await firstValueFrom(
        this.http.post<PaymentInfo>(
          this.tokenStatusUrl, payload, {
            withCredentials: true,
            observe: "response",
          },
        ),
      );

      if (response.status === 200) {
        return response.body!;
      } else {
        console.warn("Unexpected response status:", response);
        return response.body!;
      }
    } catch (error) {
      console.error("Error fetching payment response:", error);
      throw error;
    }
  }
}


export class LoadingSpinnerStatus {
  isMedicationDataLoading: boolean;
  isDevicesDataLoading: boolean;
  isObservationsDataLoading: boolean;
  isHealthPlanDataLoading: boolean;
}
